import './processPolyfill';
import React, { Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from "react-redux";
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import store from './redux/';
import * as serviceWorker from './serviceWorker';
import Config from './configure';
import Spinner from './components/spinner/Spinner';
import 'react-perfect-scrollbar/dist/css/styles.css';
import "./index.scss"
import "./assets/fonts/feather/css/feather.css";
import "flag-icon-css/sass/flag-icon.scss"
// Import logo CSS after SCSS to ensure overrides work
import "./layout/sidebar/navlogo/navLogo.css"

// Self-heal stale deploys: when a lazily-loaded chunk fails (an old tab/index
// referencing files a newer deploy removed), reload once to pull the fresh
// index.html — users never have to clear cache. A short-lived sessionStorage
// guard prevents reload loops if the failure is genuinely persistent.
if (typeof window !== 'undefined') {
  const RELOAD_GUARD = '__chunk_reload_ts';
  const isChunkError = (err) => {
    const msg = (err && (err.message || String(err))) || '';
    const name = (err && err.name) || '';
    return name === 'ChunkLoadError'
      || /Loading chunk [\w]+ failed/i.test(msg)
      || /Loading CSS chunk/i.test(msg)
      || /Failed to fetch dynamically imported module/i.test(msg)
      || /Importing a module script failed/i.test(msg);
  };
  const recover = (err) => {
    if (!isChunkError(err)) return;
    const last = Number(sessionStorage.getItem(RELOAD_GUARD) || 0);
    // Allow at most one auto-reload per 10s window, so we never loop.
    if (Date.now() - last > 10000) {
      sessionStorage.setItem(RELOAD_GUARD, String(Date.now()));
      window.location.reload();
    }
  };
  window.addEventListener('error', (e) => recover(e.error || e));
  window.addEventListener('unhandledrejection', (e) => recover(e.reason));
}

// Prevent gtag from executing in admin pages
if (typeof window !== 'undefined') {
  // Override gtag function to prevent tracking
  window.gtag = function() {
    // Do nothing - prevent gtag from tracking
  };
  
  // Clear dataLayer
  if (window.dataLayer) {
    window.dataLayer = [];
  }
  
  // Remove any gtag scripts that might be added dynamically
  const removeGtagScripts = () => {
    const scripts = document.querySelectorAll('script[src*="googletagmanager.com"], script[src*="google-analytics.com"]');
    scripts.forEach(script => script.remove());
  };
  
  // Remove existing gtag scripts
  removeGtagScripts();
  
  // Monitor for dynamically added gtag scripts
  const observer = new MutationObserver(() => {
    removeGtagScripts();
  });
  
  observer.observe(document.head, { childList: true, subtree: true });
  observer.observe(document.body, { childList: true, subtree: true });
}

const App = lazy(() => import("./App/App"))

const container = document.getElementById("root");
const root = createRoot(container);
root.render(
  <Provider store={store}>
    <BrowserRouter basename={Config.basename}>
      <AuthProvider>
        <Suspense fallback={<Spinner />}>
          <App />
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  </Provider>
);


// react-scripts 5 no longer emits a service worker, so register() only 404s on
// /service-worker.js and leaves stale workers from old deploys controlling the
// page (causing ChunkLoadError after each release). unregister() actively evicts
// any leftover worker from visitors' browsers. Do NOT switch back to register()
// unless a real service worker is being generated again.
serviceWorker.unregister();
